import { Controller } from "@hotwired/stimulus"
import { Editor, getSchema } from "@tiptap/core"
import StarterKit from "@tiptap/starter-kit"
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table"
import Placeholder from "@tiptap/extension-placeholder"
import { ListItem } from "@tiptap/extension-list"
import * as Y from "yjs"
import Collaboration, { isChangeOrigin } from "@tiptap/extension-collaboration"
import CollaborationCaret from "@tiptap/extension-collaboration-caret"
import { prosemirrorJSONToYDoc } from "@tiptap/y-tiptap"
import { CableProvider } from "../collab/cable_provider"
import { hueToHex, renderPeers } from "../collab/presence"

// Editing a document the way you edit one anywhere: type into the page. The page
// is a ProseMirror editor (Tiptap) restricted to exactly what a document can hold
// - headings 1-3, paragraphs, bold, bullet lists, tables, page breaks - so nothing
// can be typed that would be lost. What it edits is a picture of the document
// (AssistantDocuments::RichText on the server builds it and reads it back); the
// editor never decides what a document means.
//
// It saves as you go: a couple of seconds after you stop typing the whole
// document is PATCHed, and the server folds a person's saves into one version
// until they pause (so a session of editing is one version, not hundreds). If
// the document moved on underneath you - a teammate, or Jane, saved a newer
// version - saving stops and says so rather than overwriting it.
//
// With several people in the same document (a CollabChannel room, Yjs over ActionCable) the page is one shared
// document: everyone's typing merges as it happens and each person's cursor shows to the others. The title and the
// options (numbering, orientation, font) are shared too. Saving is the same: every editor saves the one merged
// document, so there is nothing to conflict with. If the room cannot be reached the editor works alone, as before.
const AUTOSAVE_MS = 1500
const JOIN_TIMEOUT_MS = 3000

export default class extends Controller {
    static targets = ["surface", "toolbar", "title", "numbered", "landscape", "status", "heading", "font", "page", "linkForm", "linkInput", "presence"]
    static values = { doc: Object, saveUrl: String, baseVersion: Number, panel: Boolean, viewUrl: String, panelUrl: String, documentTitle: String, content: Object, collabKind: String, collabId: Number, user: Object }

    connect() {
        this.titleTarget.value = this.contentValue.title || ""
        this.numberedTarget.checked = this.contentValue.number_sections === true
        this.landscapeTarget.checked = this.contentValue.landscape === true
        this.fontTarget.value = this.contentValue.font || "app"
        this._applyFont()
        this._applyNumbering()
        this._dirty = false
        this._rev = 0
        this._saving = false
        this._blocked = false
        this._disconnected = false
        this._status("Opening…")
        this._boot()
    }

    async _boot() {
        const collab = await this._joinRoom()
        if (this._disconnected) {
            this._leaveRoom(collab)
            return
        }

        this._collab = collab
        if (collab) this._bindShared()
        this.editor = new Editor({
            element: this.surfaceTarget,
            // Alone, the editor starts from the document; together, the shared document is the content.
            ...(collab ? {} : { content: this.docValue }),
            extensions: this._extensions(collab),
            editorProps: {
                handleKeyDown: (_view, event) => {
                    if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return false

                    event.preventDefault()
                    this.linkBar()
                    return true
                },
                attributes: { class: "document-rich-surface", "aria-label": "Document", role: "textbox", "aria-multiline": "true" }
            },
            // What other people typed is saved by them; only your own changes make this editor unsaved.
            onUpdate: ({ transaction }) => { if (!(this._collab && isChangeOrigin(transaction))) this._changed() },
            onSelectionUpdate: () => this._syncToolbar(),
            onTransaction: () => this._syncToolbar()
        })
        this._onBeforeUnload = (event) => { if (this._dirty) { event.preventDefault(); event.returnValue = "" } }
        window.addEventListener("beforeunload", this._onBeforeUnload)
        this._syncToolbar()
        this._status("Saved")
    }

    // The editor's extensions. Together, the shared document brings its own undo (that of your own typing only) and
    // draws everyone's cursors.
    _extensions(collab) {
        const extensions = [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                strike: false, underline: false, code: false,
                codeBlock: false, blockquote: false, listItem: false,
                ...(collab ? { undoRedo: false } : {}),
                // Links are web addresses only, and are followed on purpose, not by a stray click while editing.
                link: { openOnClick: false, autolink: true, protocols: ["http", "https"], HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }
            }),
            // A bullet holds one paragraph - no nested lists, no tables inside a bullet
            // - because a document's bullets are lines of text.
            ListItem.extend({ content: "paragraph" }),
            Table.configure({ resizable: false }),
            TableRow, TableHeader, TableCell,
            Placeholder.configure({ placeholder: "Start writing…" })
        ]
        if (collab) {
            extensions.push(
                Collaboration.configure({ document: collab.ydoc }),
                CollaborationCaret.configure({ provider: collab.provider, user: collab.user })
            )
        }
        return extensions
    }

    // --- editing together ---

    // Joins the document's room: the state it has, or, in an empty room, this page's document as the start of it.
    // Null when there is no room to join (or it cannot be reached in time): the editor then works alone.
    async _joinRoom() {
        if (!this.hasCollabKindValue || !this.hasCollabIdValue || !this.userValue?.name) return null

        try {
            const ydoc = new Y.Doc()
            const user = { name: this.userValue.name, color: hueToHex(this.userValue.hue), avatar: this.userValue.avatar_url }
            const provider = new CableProvider(ydoc, { kind: this.collabKindValue, id: this.collabIdValue, user, onReset: () => this._reload() })
            const state = await Promise.race([provider.ready, new Promise((resolve) => setTimeout(() => resolve(null), JOIN_TIMEOUT_MS))])
            if (!state?.writable) {
                provider.destroy()
                ydoc.destroy()
                return null
            }

            if (state.empty) {
                const start = prosemirrorJSONToYDoc(getSchema(this._extensions(false)), this.docValue, "default")
                const meta = start.getMap("meta")
                meta.set("title", this.contentValue.title || "")
                meta.set("number_sections", this.contentValue.number_sections === true)
                meta.set("landscape", this.contentValue.landscape === true)
                meta.set("font", this.contentValue.font || "app")
                await provider.seed(Y.encodeStateAsUpdate(start))
            }
            return { ydoc, provider, user }
        } catch (_error) {
            return null
        }
    }

    // The title and options live in the shared document as well, and the page's controls follow them.
    _bindShared() {
        const meta = this._collab.ydoc.getMap("meta")
        const apply = () => {
            if (meta.has("title") && this.titleTarget.value !== meta.get("title")) this.titleTarget.value = meta.get("title")
            if (meta.has("number_sections")) this.numberedTarget.checked = meta.get("number_sections") === true
            if (meta.has("landscape")) this.landscapeTarget.checked = meta.get("landscape") === true
            if (meta.has("font")) this.fontTarget.value = meta.get("font")
            this._applyFont()
            this._applyNumbering()
        }
        meta.observe((_event, transaction) => { if (!transaction.local) apply() })
        apply()
        this._collab.provider.awareness.on("change", () => this._renderPresence())
        this._renderPresence()
    }

    _setShared(values) {
        if (!this._collab) return

        const meta = this._collab.ydoc.getMap("meta")
        this._collab.ydoc.transact(() => Object.entries(values).forEach(([key, value]) => { if (meta.get(key) !== value) meta.set(key, value) }))
    }

    // Who else is in the document, beside the status.
    _renderPresence() {
        if (this._collab) renderPeers(this.hasPresenceTarget ? this.presenceTarget : null, this._collab.provider.awareness, this._collab.ydoc.clientID)
    }

    // The document was changed from outside the live editor (Jane revised it, a version was restored): what is on
    // this page is stale, so show the new one.
    _reload() {
        this._dirty = false
        this._blocked = true
        this._status("This document was just changed elsewhere. Reloading…", true)
        setTimeout(() => window.location.reload(), 1200)
    }

    _leaveRoom(collab) {
        if (!collab) return

        collab.provider.destroy()
        collab.ydoc.destroy()
    }

    disconnect() {
        this._disconnected = true
        clearTimeout(this._timer)
        window.removeEventListener("beforeunload", this._onBeforeUnload)
        this.editor?.destroy()
        this._leaveRoom(this._collab)
    }

    // --- toolbar ---

    bold() { this.editor.chain().focus().toggleBold().run() }
    italic() { this.editor.chain().focus().toggleItalic().run() }
    numbers() { this.editor.chain().focus().toggleOrderedList().run() }
    bullets() { this.editor.chain().focus().toggleBulletList().run() }
    undo() { this.editor.chain().focus().undo().run() }
    redo() { this.editor.chain().focus().redo().run() }
    pageBreak() { this.editor.chain().focus().setHorizontalRule().run() }
    insertTable() { this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() }
    addRow() { this.editor.chain().focus().addRowAfter().run() }
    addColumn() { this.editor.chain().focus().addColumnAfter().run() }
    deleteRow() { this.editor.chain().focus().deleteRow().run() }
    deleteColumn() { this.editor.chain().focus().deleteColumn().run() }
    deleteTable() { this.editor.chain().focus().deleteTable().run() }

    setBlock(event) {
        const value = event.currentTarget.value
        const chain = this.editor.chain().focus()
        if (value === "paragraph") chain.setParagraph().run()
        else chain.setHeading({ level: Number(value) }).run()
    }

    // The link bar: a web address for the selected text (or the link under the cursor).
    linkBar() {
        if (!this.linkFormTarget.hidden) return this.closeLink()

        this.linkInputTarget.value = this.editor.getAttributes("link").href || ""
        this.linkFormTarget.hidden = false
        this.linkInputTarget.focus()
    }

    applyLink(event) {
        event.preventDefault()
        let href = this.linkInputTarget.value.trim()
        if (!href) return this.removeLink()

        // A path inside the app ("/tasks?list=449") stays as it is; anything else without a scheme is a web address.
        if (!/^https?:\/\//i.test(href) && !/^\/(?!\/)/.test(href)) href = `https://${href}`
        const chain = this.editor.chain().focus().extendMarkRange("link")
        if (this.editor.state.selection.empty && !this.editor.isActive("link")) {
            chain.insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run()
        } else {
            chain.setLink({ href }).run()
        }
        this.closeLink()
    }

    removeLink() {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run()
        this.closeLink()
    }

    closeLink() {
        this.linkFormTarget.hidden = true
        this.editor.commands.focus()
    }

    optionsChanged() {
        this._applyNumbering()
        this._setShared({ number_sections: this.numberedTarget.checked, landscape: this.landscapeTarget.checked })
        this._changed()
    }

    fontChanged() {
        this._applyFont()
        this._setShared({ font: this.fontTarget.value })
        this._changed()
    }

    titleChanged() {
        this._setShared({ title: this.titleTarget.value })
        this._changed()
    }

    // --- saving ---

    async done() {
        clearTimeout(this._timer)
        if (this._dirty && !this._blocked) await this._save()
        if (this._dirty) return // a refused save keeps you here, with the reason showing

        this._leave()
    }

    _changed() {
        this._dirty = true
        this._rev += 1
        this._status("Unsaved changes…")
        clearTimeout(this._timer)
        if (!this._blocked) this._timer = setTimeout(() => this._save(), AUTOSAVE_MS)
    }

    async _save() {
        if (this._saving) {
            this._again = true
            return
        }

        this._saving = true
        this._status("Saving…")
        const rev = this._rev
        try {
            const response = await fetch(this.saveUrlValue, {
                method: "PATCH",
                credentials: "same-origin",
                headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
                body: JSON.stringify({
                    doc: this.editor.getJSON(), title: this.titleTarget.value.trim(),
                    number_sections: this.numberedTarget.checked, landscape: this.landscapeTarget.checked, font: this.fontTarget.value,
                    base_version: this.baseVersionValue, coalesce: true, collab: this._collab ? "1" : undefined
                })
            })
            const data = await response.json().catch(() => ({}))
            if (response.ok) {
                this.baseVersionValue = data.version
                // Edited again while this save was on its way: not clean yet.
                this._dirty = this._rev !== rev
                this._status(this._dirty ? "Unsaved changes…" : "Saved")
            } else {
                this._blocked = response.status === 409
                this._status(data.error || "That didn't save.", true)
            }
        } catch (_error) {
            this._status("Couldn't save - check your connection.", true)
        } finally {
            this._saving = false
            if (this._again && !this._blocked) {
                this._again = false
                this._save()
            }
        }
    }

    // "Number the clauses" shows 1. / 1.1 / 1.1.1 on the headings as you edit, as the
    // finished document will print them (the numbers are CSS counters, never text).
    _applyNumbering() {
        this.pageTarget.classList.toggle("document-rich-page--numbered", this.numberedTarget.checked)
    }

    // The page is drawn in the chosen font as you pick it, the same stacks the
    // finished sheet uses (document-rich-page--font-<key>).
    _applyFont() {
        this.pageTarget.className = this.pageTarget.className.replace(/\bdocument-rich-page--font-\S+/g, "").trim()
        this.pageTarget.classList.add(`document-rich-page--font-${this.fontTarget.value}`)
    }

    _leave() {
        if (this.panelValue) {
            window.dispatchEvent(new CustomEvent("jane:open-panel", {
                detail: { url: this.panelUrlValue, title: this.documentTitleValue, icon: "bi-file-earmark-word", label: "Document" }
            }))
        } else {
            window.location.href = this.viewUrlValue
        }
    }

    // --- toolbar state ---

    _syncToolbar() {
        if (!this.editor || !this.hasToolbarTarget) return

        const set = (name, active) => this.toolbarTarget.querySelectorAll(`[data-tool="${name}"]`).forEach((el) => {
            el.classList.toggle("is-active", !!active)
            el.setAttribute("aria-pressed", String(!!active))
        })
        set("bold", this.editor.isActive("bold"))
        set("italic", this.editor.isActive("italic"))
        set("link", this.editor.isActive("link"))
        set("bullets", this.editor.isActive("bulletList"))
        set("numbers", this.editor.isActive("orderedList"))
        const inTable = this.editor.isActive("table")
        this.toolbarTarget.querySelectorAll("[data-table-only]").forEach((el) => { el.hidden = !inTable })
        if (this.hasHeadingTarget) {
            const level = [1, 2, 3].find((n) => this.editor.isActive("heading", { level: n }))
            this.headingTarget.value = level ? String(level) : "paragraph"
        }
    }

    _status(text, problem = false) {
        if (!this.hasStatusTarget) return

        this.statusTarget.textContent = text
        this.statusTarget.classList.toggle("is-problem", problem)
    }

    _csrf() {
        return document.querySelector("meta[name='csrf-token']")?.content || ""
    }
}
